import {
  GlobalWorkerOptions,
  PDFLoadingTask,
  PDFDocumentProxy,
  PDFSource,
} from "pdfjs-dist";
import { LoggerFactory } from "../../../utilities/logger";

// 基本的には@types/pdfjs-distを使用したいが、型が違いすぎるので使用する部分だけを抽出する
interface IPdfjsLib {
  getDocument(source: PDFSource): PDFLoadingTask<IPDFDocumentProxy>;
  GlobalWorkerOptions: IGlobalWorkerOptions;
}

interface IGlobalWorkerOptions extends GlobalWorkerOptions {
  workerPort: any;
} 

interface IPDFDocumentProxy extends PDFDocumentProxy {
  cleanup(): void;
} 

interface IPdfjsViewer {
  PDFViewer: IPDFViewer;
  PDFSinglePageViewer: IPDFViewer;  // ページ単位の表示を行いたい場合使用する。読み込み中に2ページ目に切替を行うと画面が崩れる等、使用時には要確認。
}

interface IPDFViewer {
  new (params: {
    container: HTMLElement,
    maxCanvasPixels: number,
    viewer?: HTMLElement,           // デフォルトはcontainer.firstElementChild
    textLayerMode?: TextLayerMode,  // デフォルトはENABLE。選択不可にする場合DISABLEにすれば実現可能
    useOnlyCssZoom?: boolean,       // デフォルトはfalse。trueは縮小時の品質は良いが、拡大するとボケて見れる。falseは縮小時につぶれて見えるが、拡大時ははっきり見える。また、true時IEで横フィット時に不要なスクロールが表示されてしまう問題あり
  }): this;

  setDocument(pdfDocument: IPDFDocumentProxy): void;
  scrollPageIntoView(params: {
    pageNumber: number;
  }): void;
  update(): void;
  focus(): void;
  isPageVisible(pageNumber: number): boolean;
  cleanup(): void;
  getPagesOverview(): {
    width: number,
    height: number,
    rotation: number,
  }[];

  readonly pagesCount: number;
  readonly pageViewsReady: boolean;
  currentPageNumber: number;
  currentScale: number;
  currentScaleValue: ScaleValueType;
  pagesRotation: number;
  readonly isInPresentationMode: boolean;
  readonly isChangingPresentationMode: boolean;
  readonly isHorizontalScrollbarEnabled: boolean;
  readonly isVerticalScrollbarEnabled: boolean;
  readonly hasEqualPageSizes: boolean;
  scrollMode: ScrollMode; // スクロールモード（縦、横、折り返し）
  spreadMode: SpreadMode; // 見開きモード（奇数ページ、偶数ページ）

  container: HTMLElement;
  eventBus: IEventBus;
}

interface IEventBus {
  on(eventName: string, listener: (args?: any) => void): void;
  off(eventName: string, listener: (args?: any) => void): void;
  dispatch(eventName: string, args?: any): void;
}

type ScaleValueType = "" | "page-actual" | "page-width" | "page-height" | "page-fit" | "auto";

enum TextLayerMode {
  DISABLE = 0,
  ENABLE = 1,
  ENABLE_ENHANCE = 2,
}

enum ScrollMode {
  UNKNOWN = -1,
  VERTICAL = 0,
  HORIZONTAL = 1,
  WRAPPED = 2,
}

enum SpreadMode {
  UNKNOWN = -1,
  NONE = 0,
  ODD = 1,
  EVEN = 2,
}

// tslintに指摘されるためコメントは必要
const pdfjsLib: IPdfjsLib = process.env.NODE_ENV !== "production"
  // tslint:disable-next-line:no-var-requires
  ? require("pdfjs-dist/webpack")
  // tslint:disable-next-line:no-var-requires
  : require("pdfjs-dist");
// tslint:disable-next-line:no-var-requires
const pdfjsViewer: IPdfjsViewer = require("pdfjs-dist/web/pdf_viewer");

const maxCanvasPixels: number = 16777216;

// cMap設定(文字が認識できない時に読み込まれるファイル。動作未確認)
const cMapUrl: string = "static/cmaps/";
const cMapPacked: boolean = true;

// worker設定
if (process.env.NODE_ENV === "production" && typeof window !== "undefined" && "Worker" in window) {
  // tslint:disable-next-line:no-var-requires
  const PdfjsWorker = require("worker-loader?name=static/js/[hash].worker.js!pdfjs-dist/build/pdf.worker");
  pdfjsLib.GlobalWorkerOptions.workerPort = new PdfjsWorker();
}

export class PdfjsAccessor {
  // disableFontFaceについて
  // OpenTypeフォントに変換しないようにする。
  // IEはtrueにした方が綺麗。Chromeはあまり変わらないがfalseの方が見やすいかも？
  constructor(
      private container: HTMLDivElement,
      private isPDFSinglePageViewer: boolean = false,
      private useOnlyCssZoom: boolean = false,
      private disableFontFace: boolean = false) {
    const params = {
      container: this.container.parentElement,
      maxCanvasPixels: maxCanvasPixels,
      useOnlyCssZoom: this.useOnlyCssZoom,
    };
    this.pdfViewer = this.isPDFSinglePageViewer
      ? new pdfjsViewer.PDFSinglePageViewer(params)
      : new pdfjsViewer.PDFViewer(params);
    this.pdfViewer.eventBus.on("pagerendered", () => {
      // 描画が完了したことをPDFチェックツールで検知させるため、描画が完了したときにログを追加している。
      LoggerFactory.getLogger().debug("PDF.jsでPDFのページを描画しました。");
      LoggerFactory.getLogger().debug("[PDF.js] pagerendered.");
    });
  }
  private pdfViewer: IPDFViewer;
  private pdfDocument?: IPDFDocumentProxy;
  private activeUrl: string = "";
  private waitUrl: string = "";

  get numPages(): number {
    return this.pdfDocument
      ? this.pdfDocument.numPages
      : 0;
  }

  get currentPageNumber(): number {
    return this.pdfViewer.currentPageNumber;
  }

  set currentPageNumber(pageNumber: number) {
    try {
      if (this.pdfViewer.currentPageNumber === pageNumber) {
        return;
      }
      if (this.isPDFSinglePageViewer) {
        // 表示中にページを切り替えると画面が崩れる場合があるための対策
        this.pdfViewer.cleanup();
      }
      this.pdfViewer.scrollPageIntoView({ pageNumber: pageNumber });
    } catch (e) {
      console.error(e);
    }
  }

  get currentScale(): number {
    return this.pdfViewer.currentScale;
  }

  set currentScale(scale: number) {
    try {
      this.pdfViewer.currentScale = scale;
    } catch (e) {
      console.error(e);
    }
  }

  get currentScaleValue(): ScaleValueType {
    return this.pdfViewer.currentScaleValue;
  }

  set currentScaleValue(value: ScaleValueType) {
    try {
      if (!this.container.offsetParent) { // 親要素のdisplayがnodeの場合、nullになる
        return;
      }
      this.currentScale = 10; // 縮小状態から設定すると正しいサイズが算出されないため一度拡大させる
      this.pdfViewer.currentScaleValue = value;
    } catch (e) {
      console.error(e);
    }
  }

  get pagesRotation(): number {
    return this.pdfViewer.pagesRotation;
  }

  set pagesRotation(rotate: number) {
    try {
      this.pdfViewer.pagesRotation = rotate;
    } catch (e) {
      console.error(e);
    }
  }

  async loadDocument(url: string): Promise<boolean> {
    try {
      if (this.activeUrl !== "") {
        this.waitUrl = url;
        return false;
      }
      this.activeUrl = url;
      this.waitUrl = "";
      this.cleanupDocument();
      this.pdfDocument = await pdfjsLib.getDocument({
        url: this.activeUrl,
        cMapUrl: cMapUrl,
        cMapPacked: cMapPacked,
        disableFontFace: this.disableFontFace,
        isEvalSupported: false,
      }).promise;
      if (this.waitUrl !== "") {
        this.activeUrl = "";
        return this.loadDocument(this.waitUrl);
      }
      LoggerFactory.getLogger().debug("PDF.jsでPDFを表示します。URL=" + this.activeUrl);
      this.pdfViewer.setDocument(this.pdfDocument);
      await this.waitForPagesInit();
      this.activeUrl = "";
      return this.waitUrl !== ""
        ? this.loadDocument(this.waitUrl)
        : true;
    } catch (e) {
      console.error(e);
      this.activeUrl = "";
      return this.waitUrl !== ""
        ? this.loadDocument(this.waitUrl)
        : true;
    }
  }

  private async waitForPagesInit(): Promise<void> {
    return new Promise<void>(resolve => {
      const func = () => {
        this.pdfViewer.eventBus.off("pagesinit", func);
        resolve();
      };
      this.pdfViewer.eventBus.on("pagesinit", func);
    });
  }

  cleanupDocument(): void {
    this.pdfViewer.cleanup();

    if (this.pdfDocument) {
      this.pdfDocument.cleanup();
      delete this.pdfDocument;
    }

    this.pdfViewer.setDocument(null);
  }
}
